import * as React from "react";
import classnames from "classnames";
import { useOnClickOutside } from "@grandvisionhq/lib-utils-react";
import { useIntl } from "@grandvisionhq/state";
import useCursor from "../hooks/Autocomplete/hooks/use-cursor.js";
import useIsOpenSuggestions from "../hooks/Autocomplete/hooks/use-is-open-suggestions.js";
import Input from "../Input/Autocomplete/Input/Input.js";
import Suggestions from "../Suggestions/Autocomplete/Suggestions/Suggestions.js";
/* empty css                  */
import { isTouchDevice } from "../../Util/Util/is-touch-device.js";
const assignCursorNumberToSuggestion = (suggestions, hasTopContent) => suggestions.map((suggestion, index) => ({
  ...suggestion,
  cursor: hasTopContent ? index + 1 : index
}));
const Autocomplete = ({
  className,
  disabled = false,
  handleClickOutside,
  isFocusedOnMount,
  isInvalid,
  isSuggestionSelected,
  isValid,
  labels,
  loading,
  minCountCharactersToSubmit = 0,
  name,
  onChange,
  onClear,
  onSubmit,
  onSuggestionClick,
  onSuggestionTopContentClick,
  setIsTouched,
  showClearButton,
  submitIcon,
  suggestions,
  suggestionTopContent,
  type = "text",
  value,
  inputRef = React.createRef(),
  inputDataT,
  ...rest
}) => {
  const { getLabel } = useIntl();
  const ref = React.useRef(null);
  const hasTopContent = Boolean(suggestionTopContent);
  const hasFocusedOnMount = React.useRef(false);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useIsOpenSuggestions(inputRef);
  const suggestionsWithCursor = React.useMemo(
    () => assignCursorNumberToSuggestion(suggestions, hasTopContent),
    [suggestions, hasTopContent]
  );
  useOnClickOutside(ref, () => {
    setIsSuggestionsOpen(false);
    handleClickOutside && handleClickOutside(value);
  });
  const { cursor, setCursor, setHovered } = useCursor({
    ref: inputRef,
    suggestions: suggestionsWithCursor,
    hasTopContent,
    onSuggestionClick,
    onSuggestionTopContentClick,
    onSubmit,
    setIsSuggestionsOpen
  });
  const showSuggestions = !isSuggestionSelected ? value.length > minCountCharactersToSubmit || suggestions.length > 0 : false;
  const handleOnClick = React.useCallback(() => {
    setIsSuggestionsOpen(showSuggestions);
  }, [showSuggestions, setIsSuggestionsOpen]);
  const handleOnChange = React.useCallback(
    (event) => {
      onChange(event);
    },
    [onChange]
  );
  const handleOnBlur = React.useCallback(() => {
    if (cursor === (hasTopContent ? suggestions.length : suggestions.length - 1)) {
      setCursor(-1);
      setIsSuggestionsOpen(false);
    }
  }, [cursor, hasTopContent, suggestions.length, setCursor, setIsSuggestionsOpen]);
  React.useEffect(() => {
    if (isFocusedOnMount && hasFocusedOnMount.current === false && isTouchDevice() === false) {
      inputRef.current?.focus();
      hasFocusedOnMount.current = true;
    }
  }, [inputRef, isFocusedOnMount]);
  React.useEffect(() => {
    setIsSuggestionsOpen(showSuggestions);
  }, [isSuggestionSelected, suggestions]);
  const screenReaderLabels = React.useMemo(
    () => ({
      clearButtonScreenReaderHint: getLabel("elements.autocomplete.clear.button"),
      submitButtonScreenReaderHint: labels.submitButtonScreenReaderHint ?? getLabel("elements.autocomplete.search.button"),
      // noAvailableResultsScreenReaderHint can be set when, for example, the first option is set to Use My Location even when there are no results.
      noAvailableResultsScreenReaderHint: labels.noAvailableResultsScreenReaderHint,
      availableResultsScreenReaderHint: getLabel(
        "elements.autocomplete.availableResultsScreenReaderHint"
      ),
      inputScreenReaderHint: getLabel("elements.autocomplete.inputScreenReaderHint")
    }),
    [getLabel, labels.noAvailableResultsScreenReaderHint]
  );
  return /* @__PURE__ */ React.createElement("div", { ref, ...rest, className: classnames("autocomplete", className) }, /* @__PURE__ */ React.createElement(
    Input,
    {
      count: suggestionsWithCursor.length,
      cursor,
      disabled,
      isInvalid,
      isListSuggestionsOpen: isSuggestionsOpen,
      isValid,
      labels: screenReaderLabels,
      loading,
      name,
      onBlur: setIsTouched,
      onChange: handleOnChange,
      onClear,
      onClick: handleOnClick,
      onSubmit,
      placeholder: labels.placeholder,
      ref: inputRef,
      showClearButton,
      submitIcon,
      type,
      value,
      "data-t": inputDataT
    }
  ), isSuggestionsOpen && /* @__PURE__ */ React.createElement(
    Suggestions,
    {
      autocompleteName: name,
      cursor,
      noResults: labels.noResults,
      onBlur: handleOnBlur,
      onSuggestionClick,
      onSuggestionTopContentClick,
      setFocus: setCursor,
      setHovered,
      suggestions: suggestionsWithCursor,
      suggestionTopContent
    }
  ));
};
export {
  Autocomplete as default
};
//# sourceMappingURL=Autocomplete.js.map
